import { GameScene } from '../GameScene';
import { Direction } from '@shared/types/entity';

/**
 * Cámara y conversiones de coordenadas: world↔screen (worldToScreen/screenToWorld), posición world
 * del player local, predicción del próximo tile y el seguimiento directo de cámara al player
 * (updateCameraPosition, llamado por frame desde el update loop). Extraído de GameScene (god-object).
 * El estado (cámara, localPlayerFollowTarget…) vive en GameScene (host) y se accede vía `this.scene.*`.
 * Ver docs/MOVEMENT_AND_PREDICTION.md.
 */
export class CameraController {
  constructor(private scene: GameScene) {}

  public worldToScreenX(worldX: number): number {
    // Con la cámara siguiendo al personaje, Phaser convierte automáticamente
    // Pero para items/NPCs que se posicionan manualmente, usamos la posición de la cámara
    const cam = this.scene.cameras.main;
    return worldX - cam.scrollX;
  }

  public worldToScreenY(worldY: number): number {
    const cam = this.scene.cameras.main;
    return worldY - cam.scrollY;
  }

  /**
   * AO-like: Convierte coordenadas de pantalla a coordenadas de mundo
   */
  public screenToWorldX(screenX: number): number {
    const cam = this.scene.cameras.main;
    return screenX + cam.scrollX;
  }

  public screenToWorldY(screenY: number): number {
    const cam = this.scene.cameras.main;
    return screenY + cam.scrollY;
  }

  /**
   * Predicts the next tile position based on current tile and direction.
   * Used for continuous movement prediction when server reports MOVING state.
   */
  public predictNextTile(tileX: number, tileY: number, direction: Direction): { x: number; y: number } {
    switch (direction) {
      case Direction.UP:
        return { x: tileX, y: tileY - 1 };
      case Direction.DOWN:
        return { x: tileX, y: tileY + 1 };
      case Direction.LEFT:
        return { x: tileX - 1, y: tileY };
      case Direction.RIGHT:
        return { x: tileX + 1, y: tileY };
      default:
        return { x: tileX, y: tileY };
    }
  }

  /**
   * AO-like: Obtiene la posición del mundo del jugador local
   */
  public getLocalPlayerWorldPosition(): { x: number; y: number } | null {
    if (!this.scene.localSessionId) return null;
    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!render) return null;
    // Usar currentWorldX/Y que se actualiza con interpolación
    return { x: render.currentWorldX, y: render.currentWorldY };
  }

  /**
   * RENDERING FIX: Direct camera positioning for jitter-free movement.
   * 
   * Instead of using Phaser's follow system which can introduce micro-lag,
   * we directly set the camera's scroll position to center on the local player.
   * This ensures the camera and player are always perfectly synchronized.
   * 
   * For a player-centered game like Argentum Online, this approach provides
   * the smoothest possible camera movement.
   * 
   * IMPORTANTE: La cámara sigue la POSICIÓN DEL TILE del personaje, NO el sprite del body.
   * Esto evita que la cámara se mueva cuando cambia la armadura (diferentes offsets de body).
   */
  public updateCameraPosition(): void {
    if (!this.scene.localSessionId) return;
    
    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    if (!render) return;
    
    const cam = this.scene.cameras.main;
    
    // =====================================================================
    // CAMERA FOLLOWS TILE POSITION, NOT BODY SPRITE
    // =====================================================================
    // Usamos la posición del tile interpolada (currentWorldX/Y) que representa
    // el centro del tile donde está el personaje. Esto es INDEPENDIENTE del
    // body/armadura equipada, así que cambiar de armadura no mueve la cámara.
    // =====================================================================
    const tileX = Math.round(render.currentWorldX);
    const tileY = Math.round(render.currentWorldY);
    
    // Centrar cámara en la posición del tile (ignorando offsets de body)
    const targetScrollX = tileX - cam.width / 2;
    const targetScrollY = tileY - cam.height / 2;
    
    // Set camera scroll directly
    cam.setScroll(targetScrollX, targetScrollY);
  }
}
